import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoreSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [details, setDetails] = useState(null);

  const sessionId = searchParams.get("session_id");
  const directToken = searchParams.get("token");

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 6;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }
    try {
      const res = await axios.get(`${API}/store/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === "paid") {
        setDetails(data);
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (e) {
      if (attempts >= maxAttempts - 1) setStatus("failed");
      else setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  }, [sessionId]);

  useEffect(() => {
    if (directToken) {
      // PayPal flow / emailed link — token already issued
      setDetails({ download_token: directToken, product_name: null });
      setStatus("success");
      return;
    }
    if (!sessionId) {
      navigate("/store");
      return;
    }
    pollPaymentStatus();
  }, [sessionId, directToken, navigate, pollPaymentStatus]);

  const downloadUrl = details?.download_token
    ? `${API}/store/download/${details.download_token}`
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0] flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl" data-testid="store-status-card">
        <CardContent className="p-8 text-center space-y-6">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-[#04539F] mx-auto animate-spin" data-testid="loading-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="checking-message">Confirming your payment...</h2>
              <p className="text-gray-600">Please wait, this only takes a moment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" data-testid="success-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="success-message">Payment Successful!</h2>
              <p className="text-gray-600">
                Thank you for your purchase{details?.product_name ? `: ${details.product_name}` : ""}. Your download is ready below and a copy has been sent to your email.
              </p>
              {downloadUrl && (
                <a href={downloadUrl} data-testid="download-link">
                  <Button className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full flex items-center justify-center gap-2" data-testid="download-btn">
                    <Download className="w-4 h-4" /> Download Now
                  </Button>
                </a>
              )}
              <Button variant="outline" className="w-full rounded-full" onClick={() => navigate("/store")} data-testid="back-to-store-btn">
                Back to Store
              </Button>
            </>
          )}

          {(status === "failed" || status === "expired") && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto" data-testid="error-icon" />
              <h2 className="text-2xl font-bold text-gray-900" data-testid="error-message">
                {status === "expired" ? "Payment Expired" : "Payment Issue"}
              </h2>
              <p className="text-gray-600">
                We couldn't confirm your payment. If you were charged, please contact us and we'll send your download right away.
              </p>
              <Button className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full" onClick={() => navigate("/store")} data-testid="try-again-btn">
                Back to Store
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreSuccess;
